import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authReq: any;
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);

  public static isSessionExpired = false;

  public static resetSessionExpired() {
    AuthInterceptor.isSessionExpired = false;
  }

  constructor(private router: Router) {}

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    const errorPayload = err?.error?.payload;
    const errorCode = err?.error?.code;
    const errorMessage = err?.error?.message?.toLowerCase() || '';

    // -------------------------
    // IMPORTANT: Check if this is a public auth endpoint (login, signup, reset password, etc.)
    // Errors on these endpoints (e.g. wrong password on login) should be handled by the component,
    // NOT treated as session expirations.
    // -------------------------
    const url = (err.url || '').toLowerCase();
    const isPublicAuthRequest =
      url.includes('/auth/login') ||
      url.includes('/login') ||
      url.includes('/signup') ||
      url.includes('/register') ||
      url.includes('/reset-password') ||
      url.includes('/reset') ||
      url.includes('/forgot') ||
      url.includes('/verify-otp') ||
      url.includes('/send-otp') ||
      url.includes('/google') ||
      url.includes('/facebook');

    if (isPublicAuthRequest) {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // Check if user had an active session & current route
    // -------------------------
    const currentUrl = (this.router.url || '').toLowerCase();
    const isAlreadyOnAuthPage =
      currentUrl.includes('/auth/login') ||
      currentUrl.includes('/auth/signup') ||
      currentUrl.includes('/auth/reset');

    const hadToken = !!this.secureStorage.getItem('jwtToken') || !!this.secureStorage.getItem('authToken');

    // -------------------------
    // Handle 401 / 403 / Token Expired
    // -------------------------
    const isTokenError = (
      errorMessage.includes('token expired') ||
      errorMessage.includes('invalid token') ||
      errorMessage.includes('token is expired') ||
      errorMessage.includes('jwt expired') ||
      errorMessage.includes('unauthorized access') ||
      errorMessage.includes('different datasource') ||
      errorMessage.includes('datasource')
    );

    if (
      (err.status === 401 || errorCode === 'ERROR_CODE_UNAUTHORIZED') &&
      (isTokenError || errorMessage.includes('datasource'))
    ) {
      if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
        AuthInterceptor.isSessionExpired = true;

        this.clearStorage();
        this.sharedService.hideLoader();
        this.sharedService.closeAllModals();

        const message = errorMessage.includes('datasource')
          ? 'Invalid authentication token. Please login again.'
          : 'Your session has expired. Please login again.';

        this.sharedService.createNotification('error', message);
        this.router.navigate(['/auth/login']);
      } else {
        this.clearStorage();
        this.sharedService.hideLoader();
      }

      return EMPTY;
    }

    // -------------------------
    // Handle 403 separately (Access Forbidden)
    // -------------------------
    if (err.status === 403) {
      if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
        this.handleAccessDenied(errorPayload);
        return throwError(() => err);
      }

      if (isTokenError) {
        if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
          AuthInterceptor.isSessionExpired = true;
          this.clearStorage();
          this.sharedService.hideLoader();
          this.sharedService.closeAllModals();

          this.sharedService.createNotification(
            'error',
            'Your session has expired. Please login again.'
          );
          this.router.navigate(['/auth/login']);
        } else {
          this.clearStorage();
          this.sharedService.hideLoader();
        }

        return EMPTY;
      }
    }

    // -------------------------
    // Onboarding-related redirects
    // -------------------------
    if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
      this.handleAccessDenied(errorPayload);
      return throwError(() => err);
    }

    // -------------------------
    // Handle Fatal Error
    // -------------------------
    if (errorCode === 'ERROR_FATAL') {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // Handle generic 401 errors
    // -------------------------
    if (err.status === 401) {
      if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
        AuthInterceptor.isSessionExpired = true;
        this.clearStorage();
        this.sharedService.hideLoader();
        this.sharedService.closeAllModals();

        this.sharedService.createNotification(
          'error',
          err?.error?.message || 'Authentication failed. Please login again.'
        );
        this.router.navigate(['/auth/login']);
      } else {
        this.clearStorage();
        this.sharedService.hideLoader();
      }

      return EMPTY;
    }

    // -------------------------
    // Other errors - Let the component handle them
    // -------------------------
    this.sharedService.hideLoader();
    return throwError(() => err);
  }

  private handleAccessDenied(errorPayload: any): void {
    this.sharedService.hideLoader();

    if (errorPayload) {
      if (
        errorPayload.company_status === "user don't have a registered company"
      ) {
        this.sharedService.createNotification(
          'info',
          'Please complete your company setup'
        );
        setTimeout(() => this.router.navigate(['/auth/setup-company']), 2000);
      } else if (
        errorPayload.subscription_status === 'user in not subscribed yet'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please subscribe to a plan'
        );
        setTimeout(() => this.router.navigate(['/auth/choose-plan']), 2000);
      } else if (
        errorPayload.verification_status ===
        'user need to verify there account using top'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please verify your account using OTP'
        );
        setTimeout(() => this.router.navigate(['/auth/verify-otp']), 2000);
      } else {
        this.sharedService.createNotification(
          'error',
          'Access denied. Please contact support.'
        );
        setTimeout(() => this.router.navigate(['/auth/login']), 2000);
      }
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for AI API calls (they have their own authentication)
    if (req.url.includes('ai.luqta.io')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x)) // Don't trigger session expiry for AI API errors
      );
    }

    // Skip interceptor for ChatBolt API calls (uses x-api-key auth)
    if (req.url.includes('/api/v1/api-builder/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }
    if (req.url.includes('/api/v1/ai-endpoints/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }

    const token = this.secureStorage.getItem('jwtToken')!;
    const lang = localStorage.getItem('lang') || 'en';
    this.authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Data-Source': environment.dataSource,
        'Accept-Language': lang,
        'X-Language': lang
      },
    });

    return next.handle(this.authReq).pipe(
      catchError((x) => this.handleAuthError(x))
    );
  }

  clearStorage() {
    this.secureStorage.clearProtected();
    sessionStorage.clear();
    // Remove orphaned UUID→contestId entries from localStorage
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    Object.keys(localStorage).forEach(key => {
      if (uuidPattern.test(key)) localStorage.removeItem(key);
    });
  }
}
